import * as React from 'react';

import {
  Button,
  Divider,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import type { SxProps } from '@mui/material';
import {
  errorColor,
  gray100,
  gray90,
  primaryColor
} from '../../constants/Colors';
import { formLabelWrapper } from '../../constants/Location';

import type { FieldError } from 'react-hook-form';

type selectProps = React.ComponentProps<typeof Select>;

export interface ListType {
  disabled?: boolean;
  option: string;
  value: number | string;
}

type Props = selectProps & {
  disabled?: boolean;
  error?: boolean;
  formMb?: string;
  helperText?: string | FieldError | undefined;
  labelText?: string;
  list: ListType[];
  requiredText?: string;
  placeholder?: string | number;
  type?: string;
  setMaterialRequestModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  height?: string;
  width?: string;
  spWidth?: string;
  multiple?: boolean;
  applyMultiline?: boolean;
};

const CustomSelectField = React.forwardRef(
  (props: Props, ref): React.ReactElement => {
    const {
      disabled,
      error,
      formMb,
      helperText,
      labelText,
      list,
      placeholder,
      requiredText,
      type,
      setMaterialRequestModalOpen,
      height,
      width,
      spWidth,
      multiple,
      applyMultiline,
      ...rest
    } = props;

    function inputBorderColor(): SxProps | undefined {
      if (error) {
        return {
          '&.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
            border: `1px solid ${errorColor}`
          }
        };
      }
      return {
        '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
          {
            border: `1px solid ${gray100}`
          }
      };
    }

    const valueSwitcher = () => {
      if (multiple) {
        if (Array.isArray(rest.value)) {
          return rest.value;
        } else {
          return [0];
        }
      } else {
        if (rest.value !== undefined) {
          return [rest.value];
        } else {
          return [0];
        }
      }
    };

    const selectedValues = valueSwitcher();

    const styles = {
      formMb: { marginBottom: formMb ?? '0' },
      formLabelWrapper,
      label: {
        color: gray100,
        fontSize: '14px'
      },
      required: {
        color: errorColor,
        fontSize: '14px',
        marginLeft: '8px'
      },
      select: {
        '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
          {
            border: `1px solid ${gray100}`
          },
        '.MuiSelect-icon': {
          color: type && primaryColor
        },
        bgcolor: '#FFF',
        height:
          applyMultiline && selectedValues?.length > 1
            ? undefined
            : height ?? '40px',
        minHeight:
          applyMultiline && selectedValues?.length > 1
            ? height ?? '40px'
            : undefined,
        inputBorderColor,
        width: width ?? '100%',
        '@media screen and (max-width:600px)': {
          width: spWidth ?? '100%'
        }
      }
    };

    const textfieldHelper = (): string | undefined => {
      if (helperText === undefined) return;
      return String(helperText);
    };

    const optionsMap: { [key: string]: string } = {};
    list.forEach((item) => {
      optionsMap[item.value] = item.option;
    });

    return (
      <Typography align="left" component="div" sx={styles.formMb}>
        {labelText && (
          <Typography align="left" sx={styles.formLabelWrapper} component="div">
            <Typography sx={styles.label} component="span">
              {labelText}
            </Typography>
            {requiredText && (
              <Typography component="span" sx={styles.required}>
                {requiredText}
              </Typography>
            )}
          </Typography>
        )}
        <Typography sx={{ textAlign: 'left' }} component="div">
          <FormControl sx={{ width: '100%' }} {...(error && { error: true })}>
            <Select
              {...(disabled && { disabled })}
              {...(multiple && { multiple })}
              {...rest}
              ref={ref}
              sx={styles.select}
              value={multiple ? valueSwitcher() : valueSwitcher()[0]}
              renderValue={(selected) => {
                if (
                  multiple && Array.isArray(selected)
                    ? selected.length === 1
                    : selected === 0 || selected === '選択してください'
                ) {
                  return (
                    <Typography
                      component="p"
                      sx={{
                        color: gray90,
                        fontSize: '14px',
                        fontWeight: 'unset',
                        textAlign: 'left',
                        whiteSpace: 'break-spaces'
                      }}
                    >
                      {placeholder ?? '選択してください'}
                    </Typography>
                  );
                }
                if (
                  multiple &&
                  Array.isArray(selected) &&
                  selected.length > 0
                ) {
                  return (
                    <Typography
                      component="p"
                      sx={{
                        color: type === 'primary' ? primaryColor : gray100,
                        fontSize: '14px',
                        fontWeight: type === 'primary' ? 500 : 'unset',
                        textAlign: 'left',
                        whiteSpace: 'break-spaces'
                      }}
                    >
                      {selected
                        .filter((value) => value !== 0)
                        .map((value) => optionsMap[String(value)])
                        .join(', ')}
                    </Typography>
                  );
                }
                return (
                  <Typography
                    component="p"
                    sx={{
                      color: type === 'primary' ? primaryColor : gray100,
                      fontSize: '14px',
                      fontWeight: type === 'primary' ? 500 : 'unset',
                      textAlign: 'left',
                      whiteSpace: 'break-spaces'
                    }}
                  >
                    {optionsMap[String(selected)]}
                  </Typography>
                );
              }}
            >
              {placeholder && (
                <MenuItem
                  disabled={requiredText || multiple ? true : false}
                  value=""
                  sx={{ color: gray100 }}
                >
                  <Typography
                    component="p"
                    sx={{
                      color: gray90,
                      fontSize: '14px',
                      fontWeight: 'unset',
                      textAlign: 'left',
                      whiteSpace: 'break-spaces'
                    }}
                  >
                    {placeholder ?? '選択してください'}
                  </Typography>
                </MenuItem>
              )}
              {list.map((item) => (
                <MenuItem
                  key={item.value}
                  value={item.value}
                  sx={{ color: gray100, whiteSpace: 'normal' }}
                  {...(item.disabled && { disabled: item.disabled })}
                >
                  <Typography
                    component="p"
                    sx={{
                      color: type === 'primary' ? primaryColor : gray100,
                      fontSize: '14px',
                      fontWeight: type === 'primary' ? 500 : 'unset',
                      textAlign: 'left',
                      whiteSpace: 'break-spaces'
                    }}
                  >
                    {item.option}
                  </Typography>
                </MenuItem>
              ))}
              {type === 'material' && [
                <Divider />,
                <MenuItem
                  sx={{ color: gray100, whiteSpace: 'normal', padding: 0 }}
                >
                  <Button
                    variant="text"
                    sx={{
                      height: '36px',
                      width: '100%',
                      justifyContent: 'flex-start',
                      gap: '12px'
                    }}
                    onClick={() => {
                      setMaterialRequestModalOpen &&
                        setMaterialRequestModalOpen(true);
                    }}
                  >
                    <AddIcon sx={{ color: primaryColor }} />
                    <Typography sx={{ color: primaryColor, fontSize: '16px' }}>
                      部材名をリクエストする
                    </Typography>
                  </Button>
                </MenuItem>
              ]}
            </Select>
          </FormControl>
        </Typography>
        {error && (
          <FormHelperText error={error}>{textfieldHelper()}</FormHelperText>
        )}
      </Typography>
    );
  }
);

export default CustomSelectField;
